import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, X, Loader2, FileText, Sparkles, GitCompare, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";

/**
 * Generic decision launcher.
 * - Single-subject modes (should_i_sign, prioritize_actions): pre-selects current `doc`,
 *   optionally accepts a proposed_modifier_id for simulation modes.
 * - Multi-subject modes that consume_criteria_matrix (compare_proposals): renders a
 *   subject picker (current doc + same-type siblings), enforces 2-5 subjects.
 */
export default function DecisionLauncher({ doc }) {
  const { t } = useTranslation("components");
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [modes, setModes] = useState([]);
  const [selectedMode, setSelectedMode] = useState(null);
  const [candidates, setCandidates] = useState([]);     // modifier candidates (simulation)
  const [modifierId, setModifierId] = useState("");
  const [siblings, setSiblings] = useState([]);         // same-type docs in workspace (comparison)
  const [selectedSubjectIds, setSelectedSubjectIds] = useState([]);
  const [loadingModes, setLoadingModes] = useState(false);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [loadingSiblings, setLoadingSiblings] = useState(false);
  const [running, setRunning] = useState(false);

  const isCompare = !!selectedMode?.consumes_criteria_matrix;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoadingModes(true);
    (async () => {
      try {
        const res = await api.get("/decision-modes");
        if (!cancelled) {
          setModes(res.data || []);
          if (res.data?.length && !selectedMode) setSelectedMode(res.data[0]);
        }
      } catch (e) {
        if (!cancelled) toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load decision modes");
      } finally {
        if (!cancelled) setLoadingModes(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // When the user picks compare_proposals, seed subject list with current doc and
  // fetch other documents of the same type.
  useEffect(() => {
    if (!open) return;
    if (!isCompare) {
      setSiblings([]);
      setSelectedSubjectIds([]);
      return;
    }
    let cancelled = false;
    setLoadingSiblings(true);
    setSelectedSubjectIds(doc?.id ? [doc.id] : []);
    (async () => {
      try {
        const res = await api.get("/documents", { params: { limit: 100 } });
        const all = res.data || [];
        // Same document_type as the current doc, completed, excluding self.
        const filtered = all.filter((d) =>
          d.id !== doc?.id &&
          d.document_type === doc?.document_type &&
          d.status === "completed"
        );
        if (!cancelled) setSiblings(filtered);
      } catch {
        if (!cancelled) setSiblings([]);
      } finally {
        if (!cancelled) setLoadingSiblings(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, isCompare, doc?.id, doc?.document_type]);

  // Load modifier candidates only when the selected mode requires simulation.
  useEffect(() => {
    if (!open || !selectedMode?.requires_simulation || !doc?.id) {
      setCandidates([]);
      return;
    }
    let cancelled = false;
    setLoadingCandidates(true);
    (async () => {
      try {
        const res = await api.get(`/documents/${doc.id}/modifier-candidates`);
        if (!cancelled) setCandidates(res.data?.candidates || []);
      } catch {
        if (!cancelled) setCandidates([]);
      } finally {
        if (!cancelled) setLoadingCandidates(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, selectedMode, doc?.id]);

  const toggleSubject = (id) => {
    setSelectedSubjectIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= 5) {
        toast.warning("Max 5 subjects per comparison");
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleRun = async () => {
    if (!selectedMode || !doc?.id) return;
    setRunning(true);
    try {
      const payload = {
        mode: selectedMode.key,
        subject_doc_ids: isCompare ? selectedSubjectIds : [doc.id],
      };
      if (selectedMode.requires_simulation && modifierId) {
        payload.proposed_modifier_id = modifierId;
      }
      if (selectedMode.requires_modifier && !modifierId) {
        toast.error(`${selectedMode.display_name} needs a proposed modifier document`);
        setRunning(false);
        return;
      }
      if (isCompare && selectedSubjectIds.length < 2) {
        toast.error("Select at least 2 subjects to compare");
        setRunning(false);
        return;
      }
      const res = await api.post("/decisions", payload);
      toast.success("Decision ready");
      setOpen(false);
      navigate(`/decisions/${res.data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Decision engine failed");
    } finally {
      setRunning(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setOpen(true)}
        data-testid="decision-launcher-button"
        variant="outline"
        className="gap-1.5 h-9"
      >
        <Scale size={14} strokeWidth={1.75} />
        {t("decision.cta")}
      </Button>

      {open && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
          onClick={() => setOpen(false)}
          data-testid="decision-launcher-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-xl p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-5">
              <div>
                <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-1">Decision Engine</p>
                <h2 className="font-display text-xl font-semibold text-zinc-950">What should I decide?</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="decision-launcher-close"
                className="w-7 h-7 grid place-items-center rounded hover:bg-zinc-100"
              >
                <X size={14} />
              </button>
            </div>

            {loadingModes ? (
              <div className="py-10 grid place-items-center" data-testid="decision-launcher-loading">
                <Loader2 size={18} className="animate-spin text-zinc-400" />
              </div>
            ) : (
              <>
                <div className="space-y-2 mb-4" data-testid="decision-launcher-modes">
                  {modes.map((m) => {
                    const sel = selectedMode?.key === m.key;
                    return (
                      <button
                        key={m.key}
                        onClick={() => setSelectedMode(m)}
                        data-testid={`decision-mode-option-${m.key}`}
                        className={`w-full text-left px-3 py-2.5 rounded-lg border transition-colors ${
                          sel ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white hover:border-zinc-400"
                        }`}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          {m.consumes_criteria_matrix ? (
                            <GitCompare size={13} strokeWidth={1.75} className={sel ? "text-white" : "text-zinc-500"} />
                          ) : (
                            <Sparkles size={13} strokeWidth={1.75} className={sel ? "text-white" : "text-zinc-500"} />
                          )}
                          <span className="text-sm font-semibold">{m.display_name}</span>
                          {m.requires_simulation && (
                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${sel ? "bg-white/20 text-white" : "bg-zinc-100 text-zinc-600"}`}>simulation</span>
                          )}
                          {m.produces_ranked_subjects && (
                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${sel ? "bg-white/20 text-white" : "bg-zinc-100 text-zinc-600"}`}>ranking</span>
                          )}
                          {m.consumes_criteria_matrix && (
                            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${sel ? "bg-white/20 text-white" : "bg-emerald-100 text-emerald-700"}`}>multi-subject</span>
                          )}
                        </div>
                        <p className={`text-xs mt-1 ${sel ? "text-zinc-200" : "text-zinc-500"}`}>
                          {m.verdict_options?.length ? `Verdict: ${m.verdict_options.join(" · ")}` : "Returns a ranked action list"}
                        </p>
                      </button>
                    );
                  })}
                </div>

                {/* Multi-subject picker (compare_proposals) */}
                {isCompare && (
                  <div className="mb-4" data-testid="decision-launcher-subject-section">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium mb-2">
                      Pick 2–5 documents to compare ({selectedSubjectIds.length} selected)
                    </p>
                    <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100 max-h-64 overflow-y-auto bg-white">
                      {/* current doc — always shown first, pre-checked */}
                      <SubjectRow
                        key={doc.id}
                        d={doc}
                        checked={selectedSubjectIds.includes(doc.id)}
                        onToggle={() => toggleSubject(doc.id)}
                        isSelf
                      />
                      {loadingSiblings ? (
                        <div className="py-4 grid place-items-center"><Loader2 size={14} className="animate-spin text-zinc-400" /></div>
                      ) : siblings.length === 0 ? (
                        <p className="text-xs text-zinc-500 italic px-3 py-3">No other completed {doc?.document_type} documents found in this workspace. Upload at least one more to compare.</p>
                      ) : (
                        siblings.map((s) => (
                          <SubjectRow
                            key={s.id}
                            d={s}
                            checked={selectedSubjectIds.includes(s.id)}
                            onToggle={() => toggleSubject(s.id)}
                          />
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Modifier picker (should_i_sign only) */}
                {selectedMode?.requires_simulation && (
                  <div className="mb-4" data-testid="decision-launcher-modifier-section">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-medium mb-2">
                      {selectedMode.requires_modifier
                        ? "Required: pick the proposed modifier document"
                        : "Optional: simulate as if signing this proposed modifier"}
                    </p>
                    {loadingCandidates ? (
                      <div className="py-3 grid place-items-center"><Loader2 size={14} className="animate-spin text-zinc-400" /></div>
                    ) : candidates.length === 0 ? (
                      <p className="text-xs text-zinc-500 italic">No proposed modifier documents found in this workspace.</p>
                    ) : (
                      <select
                        value={modifierId}
                        onChange={(e) => setModifierId(e.target.value)}
                        data-testid="decision-launcher-modifier-select"
                        className="w-full h-9 px-2 rounded-lg border border-zinc-200 bg-white text-sm"
                      >
                        <option value="">
                          {selectedMode.requires_modifier
                            ? "— Pick one —"
                            : "— None (evaluate document as-is) —"}
                        </option>
                        {candidates.map((c) => (
                          <option key={c.id} value={c.id}>{c.filename}</option>
                        ))}
                      </select>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-200">
                  <Button variant="ghost" onClick={() => setOpen(false)} data-testid="decision-launcher-cancel">Cancel</Button>
                  <Button
                    onClick={handleRun}
                    disabled={running || !selectedMode
                      || (isCompare && selectedSubjectIds.length < 2)
                      || (selectedMode?.requires_modifier && !modifierId)}
                    data-testid="decision-launcher-run"
                    className="bg-zinc-950 hover:bg-zinc-800 text-white gap-1.5"
                  >
                    {running ? <><Loader2 size={13} className="animate-spin" /> Running…</> : <><FileText size={13} /> Run decision</>}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function SubjectRow({ d, checked, onToggle, isSelf }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={`decision-subject-row-${d.id}`}
      className={`w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-zinc-50 transition-colors ${checked ? "bg-zinc-50" : ""}`}
    >
      <span
        className={`shrink-0 w-4 h-4 rounded border grid place-items-center ${
          checked ? "bg-zinc-950 border-zinc-950" : "border-zinc-300 bg-white"
        }`}
      >
        {checked && <Check size={11} className="text-white" strokeWidth={2.5} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-zinc-950 truncate">{d.filename}</span>
        <span className="block text-[10px] uppercase tracking-wider text-zinc-500">
          {d.document_type} {isSelf && <span className="text-emerald-600 ml-1">· current</span>}
        </span>
      </span>
    </button>
  );
}
